import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Download, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const Footer = lazy(() => import('../components/Footer'));

/* ── Brand data ─────────────────────────────────────────────────────────── */
const COLORS = [
  { id: 'orange', name: 'Brand Orange', hex: '#F97316', rgb: '249, 115, 22',  bg: '#F97316', text: '#ffffff' },
  { id: 'navy',   name: 'Dark Navy',    hex: '#0F172A', rgb: '15, 23, 42',    bg: '#0F172A', text: '#ffffff' },
  { id: 'slate',  name: 'Slate',        hex: '#1E293B', rgb: '30, 41, 59',    bg: '#1E293B', text: '#ffffff' },
  { id: 'gray',   name: 'Light Gray',   hex: '#F8FAFC', rgb: '248, 250, 252', bg: '#F8FAFC', text: '#0F172A' },
  { id: 'white',  name: 'White',        hex: '#FFFFFF', rgb: '255, 255, 255', bg: '#FFFFFF', text: '#0F172A' },
];

const WEIGHTS = [
  { name: 'Regular',  class: 'font-normal',   weight: '400' },
  { name: 'Medium',   class: 'font-medium',   weight: '500' },
  { name: 'Semibold', class: 'font-semibold', weight: '600' },
  { name: 'Bold',     class: 'font-bold',     weight: '700' },
];

/* ── SVG logo strings for download ────────────────────────────────────────── */
const makeSVG = (bgFill, textFill, wordmarkFill, width = 220, hasBg = false) => `
<svg width="${width}" height="52" viewBox="0 0 ${width} 52" xmlns="http://www.w3.org/2000/svg">
  ${hasBg ? `<rect width="${width}" height="52" fill="${hasBg}"/>` : ''}
  <rect x="0" y="4" width="44" height="44" rx="10" fill="${bgFill}"/>
  <text x="22" y="32" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="16" fill="${textFill}" text-anchor="middle" dominant-baseline="middle">RS</text>
  <text x="56" y="28" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="24" fill="${wordmarkFill}">ReStaff</text>
</svg>`.trim();

const makeIconSVG = (bgFill, textFill, hasBg = false) => `
<svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
  ${hasBg ? `<rect width="52" height="52" fill="${hasBg}"/>` : ''}
  <rect x="4" y="4" width="44" height="44" rx="10" fill="${bgFill}"/>
  <text x="26" y="28" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="16" fill="${textFill}" text-anchor="middle" dominant-baseline="middle">RS</text>
</svg>`.trim();

const LOGOS = [
  { id: 'primary',    label: 'Primary — Color',   bg: 'bg-slate-50',  border: 'border-slate-200', svg: makeSVG('#F97316','#ffffff','#0F172A'),          icon: false },
  { id: 'dark',       label: 'Dark Version',       bg: 'bg-slate-950', border: 'border-slate-800', svg: makeSVG('#ffffff','#0F172A','#ffffff','220',false), icon: false },
  { id: 'light',      label: 'Light Version',      bg: 'bg-orange-500',border: 'border-orange-400',svg: makeSVG('#ffffff','#F97316','#ffffff','220',false), icon: false },
  { id: 'mono',       label: 'Monochrome',         bg: 'bg-white',     border: 'border-slate-200', svg: makeSVG('#0F172A','#ffffff','#0F172A'),           icon: false },
];

const ICONS = [
  { id: 'icon-color', label: 'Icon — Color',  bg: 'bg-slate-50',  border: 'border-slate-200', svg: makeIconSVG('#F97316','#ffffff') },
  { id: 'icon-dark',  label: 'Icon — Dark',   bg: 'bg-slate-950', border: 'border-slate-800', svg: makeIconSVG('#ffffff','#0F172A') },
  { id: 'icon-white', label: 'Icon — White',  bg: 'bg-orange-500',border: 'border-orange-400',svg: makeIconSVG('#ffffff','#F97316') },
  { id: 'icon-mono',  label: 'Icon — Mono',   bg: 'bg-white',     border: 'border-slate-200', svg: makeIconSVG('#0F172A','#ffffff') },
];

/* ── Helper to render inline logo from SVG string ───────────────────────── */
function InlineSVG({ svgString, className = '' }) {
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: svgString }}
    />
  );
}

function downloadSVG(svgString, filename) {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function Brand() {
  const [copied, setCopied] = React.useState(null);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-white">

      {/* ── Minimal nav ─────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>ReStaff</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">Log In</Button>
          </Link>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 text-center bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-6">
            <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Brand Guidelines</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>
            Brand Assets
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
            Download official ReStaff logos, brand colors, and typography guidelines for use in all references to our brand.
          </p>
          <div className="mt-8">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={() => {
                [...LOGOS, ...ICONS].forEach(item =>
                  downloadSVG(item.svg, `restaff-${item.id}.svg`)
                );
              }}
              data-testid="download-all-btn"
            >
              <Download size={18} className="mr-2" />
              Download All Assets
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 space-y-24">

        {/* ── Logos ──────────────────────────────────────────────────────── */}
        <section aria-label="Logo">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Logo</h2>
            <p className="mt-3 text-slate-600 text-base max-w-xl">
              Use the official ReStaff logo in all references to our brand. Maintain proper spacing and do not alter the colors, proportions, or composition.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {LOGOS.map(logo => (
              <div key={logo.id} className={`rounded-2xl border ${logo.border} overflow-hidden`}>
                <div className={`${logo.bg} flex items-center justify-center h-36 px-6`}>
                  <InlineSVG svgString={logo.svg} />
                </div>
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-100">
                  <span className="text-sm font-medium text-slate-700">{logo.label}</span>
                  <button
                    onClick={() => downloadSVG(logo.svg, `restaff-${logo.id}.svg`)}
                    className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-orange-50 hover:text-orange-600 flex items-center justify-center transition-colors"
                    title="Download SVG"
                    data-testid={`download-${logo.id}`}
                  >
                    <Download size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Symbol / Icon ──────────────────────────────────────────────── */}
        <section aria-label="Symbol">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Symbol</h2>
            <p className="mt-3 text-slate-600 text-base max-w-xl">
              The standalone RS mark can be used as a favicon, app icon, or social media avatar. Always maintain the original proportions.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {ICONS.map(icon => (
              <div key={icon.id} className={`rounded-2xl border ${icon.border} overflow-hidden`}>
                <div className={`${icon.bg} flex items-center justify-center h-28`}>
                  <InlineSVG svgString={icon.svg} />
                </div>
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-100">
                  <span className="text-xs font-medium text-slate-700">{icon.label}</span>
                  <button
                    onClick={() => downloadSVG(icon.svg, `restaff-${icon.id}.svg`)}
                    className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-orange-50 hover:text-orange-600 flex items-center justify-center transition-colors"
                    title="Download SVG"
                    data-testid={`download-${icon.id}`}
                  >
                    <Download size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Brand Colors ───────────────────────────────────────────────── */}
        <section aria-label="Brand Colors">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Brand Colors</h2>
            <p className="mt-3 text-slate-600 text-base max-w-xl">
              These are the official ReStaff colors. Always use the exact HEX values to maintain visual consistency across all materials.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {COLORS.map(color => (
              <div key={color.id} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                {/* Swatch */}
                <div
                  className="h-28 w-full"
                  style={{ backgroundColor: color.bg, border: color.hex === '#FFFFFF' ? '1px solid #e2e8f0' : 'none' }}
                />
                {/* Info */}
                <div className="bg-white px-4 py-4">
                  <p className="text-sm font-bold text-slate-900 mb-2">{color.name}</p>
                  <div className="space-y-1.5">
                    {/* HEX row */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-medium">HEX</span>
                      <button
                        onClick={() => copy(color.hex, `${color.id}-hex`)}
                        className="flex items-center gap-1 text-xs font-mono text-slate-700 hover:text-orange-600 transition-colors"
                        data-testid={`copy-${color.id}-hex`}
                      >
                        {color.hex}
                        {copied === `${color.id}-hex`
                          ? <Check size={10} className="text-green-500" />
                          : <Copy size={10} className="text-slate-400" />
                        }
                      </button>
                    </div>
                    {/* RGB row */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-medium">RGB</span>
                      <button
                        onClick={() => copy(`rgb(${color.rgb})`, `${color.id}-rgb`)}
                        className="flex items-center gap-1 text-xs font-mono text-slate-700 hover:text-orange-600 transition-colors"
                        data-testid={`copy-${color.id}-rgb`}
                      >
                        {color.rgb}
                        {copied === `${color.id}-rgb`
                          ? <Check size={10} className="text-green-500" />
                          : <Copy size={10} className="text-slate-400" />
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Typography ─────────────────────────────────────────────────── */}
        <section aria-label="Typography">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Typography</h2>
            <p className="mt-3 text-slate-600 text-base max-w-xl">
              ReStaff uses <strong>Manrope</strong> as its primary typeface across all interfaces and communications.
            </p>
          </div>

          {/* Font name callout */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-8 py-6 mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-1">Primary Typeface</p>
              <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Manrope</p>
            </div>
            <a
              href="https://fonts.google.com/specimen/Manrope"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-orange-600 font-semibold hover:underline"
            >
              View on Google Fonts →
            </a>
          </div>

          {/* Weight cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WEIGHTS.map(w => (
              <div key={w.name} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{w.name}</span>
                  <span className="text-xs font-mono text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">{w.weight}</span>
                </div>
                <p
                  className={`text-slate-900 text-xl leading-snug ${w.class}`}
                  style={{ fontFamily: 'Manrope' }}
                >
                  Manage your workforce smarter.
                </p>
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
                  <p className={`text-sm text-slate-500 ${w.class}`} style={{ fontFamily: 'Manrope' }}>
                    ABCDEFGHIJKLMNOPQRSTUVWXYZ
                  </p>
                  <p className={`text-sm text-slate-500 ${w.class}`} style={{ fontFamily: 'Manrope' }}>
                    abcdefghijklmnopqrstuvwxyz
                  </p>
                  <p className={`text-sm text-slate-500 ${w.class}`} style={{ fontFamily: 'Manrope' }}>
                    0123456789
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Usage guidelines ───────────────────────────────────────────── */}
        <section aria-label="Usage Guidelines">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Usage Guidelines</h2>
            <p className="mt-3 text-slate-600 text-base max-w-xl">
              A few rules to keep the brand looking sharp and consistent across all contexts.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { title: 'Do', items: ['Use the logo with proper clear space on all sides', 'Use the provided HEX colors exactly as specified', 'Use Manrope for all brand communications', 'Download assets from this official page'], ok: true },
              { title: "Don't", items: ["Stretch, squish, or rotate the logo", "Change logo colors or add effects", "Use the logo on low-contrast backgrounds", "Recreate or reinterpret the logo mark"], ok: false },
            ].map(col => (
              <div key={col.title} className={`rounded-2xl border p-7 ${col.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                <p className={`text-lg font-bold mb-5 ${col.ok ? 'text-green-800' : 'text-red-800'}`}>{col.title}</p>
                <ul className="space-y-3">
                  {col.items.map(item => (
                    <li key={item} className="flex items-start gap-3">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${col.ok ? 'bg-green-200' : 'bg-red-200'}`}>
                        {col.ok
                          ? <Check size={11} className="text-green-700" />
                          : <span className={`text-red-700 font-bold leading-none`} style={{ fontSize: 10 }}>✕</span>
                        }
                      </span>
                      <span className={`text-sm font-medium ${col.ok ? 'text-green-800' : 'text-red-800'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <Suspense fallback={<div className="h-40" />}>
        <Footer />
      </Suspense>

    </div>
  );
}
